import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ─── URL Card ─────────────────────────────────────────────────────────────────
function UrlCard({ url, onDelete, onAnalytics }) {
  const [copied, setCopied] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(url.shortUrl);
    setCopied(true);
    toast.success('Copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this link? This cannot be undone.')) return;
    setDeleting(true);
    await onDelete(url.id);
    setDeleting(false);
  };

  return (
    <div className="card hover:shadow-md transition-all duration-200 animate-slide-up">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-brand-600 font-bold text-base truncate">{url.shortUrl}</span>
            <button
              onClick={copy}
              className={`shrink-0 text-xs px-2.5 py-1 rounded-lg font-semibold transition-colors ${
                copied ? 'bg-green-100 text-green-700' : 'bg-brand-50 text-brand-600 hover:bg-brand-100'
              }`}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-sm text-gray-400 truncate">{url.originalUrl}</p>
          <p className="text-xs text-gray-300 mt-1">
            {new Date(url.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>

        {/* Stats + Actions */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="text-center">
            <div className="text-2xl font-black text-brand-600">{url.clickCount}</div>
            <div className="text-xs text-gray-400">clicks</div>
          </div>
          <button
            onClick={() => onAnalytics(url.shortCode)}
            className="text-sm px-3 py-2 bg-gray-50 text-gray-600 rounded-xl border border-gray-200 hover:bg-gray-100 font-semibold transition-colors"
          >
            📊 Analytics
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="text-sm px-3 py-2 bg-red-50 text-red-500 rounded-xl border border-red-100 hover:bg-red-100 font-semibold transition-colors disabled:opacity-50"
          >
            {deleting ? '...' : '🗑'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Analytics Modal ──────────────────────────────────────────────────────────
function AnalyticsModal({ code, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/urls/analytics/${code}`)
      .then(res => setData(res.data.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [code]);

  const chartData = data
    ? Object.entries(data.clicksByDate)
        .sort(([a], [b]) => a.localeCompare(b))
        .slice(-14)
        .map(([date, count]) => ({
          date: new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
          clicks: count,
        }))
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-black text-gray-900">Analytics</h2>
            <p className="text-sm text-gray-400 mt-0.5">/{code}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none transition-colors">×</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data ? (
          <>
            <div className="bg-brand-50 rounded-xl p-4 mb-6 text-center">
              <div className="text-4xl font-black text-brand-600">{data.totalClicks}</div>
              <div className="text-sm text-gray-500 mt-1">Total Clicks</div>
            </div>

            {chartData.length > 0 ? (
              <>
                <p className="text-sm font-semibold text-gray-600 mb-3">Clicks (last 14 days)</p>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={chartData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,.1)', fontSize: '13px' }}
                    />
                    <Bar dataKey="clicks" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : (
              <div className="text-center py-8 text-gray-400 text-sm">No clicks yet. Share your link to start tracking!</div>
            )}

            <div className="mt-4 p-3 bg-gray-50 rounded-xl text-xs text-gray-400 truncate">
              → {data.originalUrl}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyticsCode, setAnalyticsCode] = useState(null);
  const [search, setSearch] = useState('');

  const fetchUrls = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/urls/my`);
      setUrls(data.data);
    } catch {
      toast.error('Failed to load your links.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUrls(); }, [fetchUrls]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/urls/${id}`);
      setUrls(prev => prev.filter(u => u.id !== id));
      toast.success('Link deleted.');
    } catch {
      toast.error('Failed to delete link.');
    }
  };

  const filtered = urls.filter(u =>
    u.shortCode.toLowerCase().includes(search.toLowerCase()) ||
    u.originalUrl.toLowerCase().includes(search.toLowerCase())
  );

  const totalClicks = urls.reduce((sum, u) => sum + u.clickCount, 0);

  return (
    <main className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-gray-900">
            Welcome back, {user?.name?.split(' ')[0]} 👋
          </h1>
          <p className="text-gray-500 mt-1">Manage and track all your short links.</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Total Links', value: urls.length, icon: '🔗' },
            { label: 'Total Clicks', value: totalClicks, icon: '📈' },
            { label: 'Avg. Clicks/Link', value: urls.length ? (totalClicks / urls.length).toFixed(1) : '0', icon: '⚡' },
          ].map(({ label, value, icon }) => (
            <div key={label} className="card flex items-center gap-4">
              <div className="text-3xl">{icon}</div>
              <div>
                <div className="text-2xl font-black text-gray-900">{value}</div>
                <div className="text-sm text-gray-400">{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            className="input flex-1"
            placeholder="Search your links..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Link to="/shorten" className="btn-primary shrink-0">+ New Link</Link>
        </div>

        {/* URL List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card text-center py-16">
            <div className="text-5xl mb-4">{search ? '🔍' : '🔗'}</div>
            <h3 className="text-lg font-bold text-gray-700 mb-2">
              {search ? 'No links match your search' : 'No links yet'}
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              {search ? 'Try a different search term.' : 'Create your first short link to get started.'}
            </p>
            {!search && <Link to="/shorten" className="btn-primary">Shorten a URL →</Link>}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filtered.map(url => (
              <UrlCard
                key={url.id}
                url={url}
                onDelete={handleDelete}
                onAnalytics={setAnalyticsCode}
              />
            ))}
          </div>
        )}
      </div>

      {analyticsCode && (
        <AnalyticsModal code={analyticsCode} onClose={() => setAnalyticsCode(null)} />
      )}
    </main>
  );
}
